import { Stack } from '@mui/material'
import { AutosaveIndicator } from '@ui2/feedback'
import { Section } from '@ui2/structure'
import { DropzoneBox } from '@ui/forms'
import useDocumentAttachmentMutations from '../../hooks/useDocumentAttachmentMutations'
import { AttachmentEditing, SaveState } from '../../types'
import DocumentActions from '../DocumentActions'
import FilePropertiesForm from '../FilePropertiesForm'
import SectionPropertiesForm from '../SectionPropertiesForm'

interface Props {
  selectedFile: FileItem | null
  selectedFolder: SectionItem | null
  debouncedFileConfigSave: (data: FileItemConfig & { file?: File | null }) => void
  debouncedFolderConfigSave: (data: FolderItemConfig) => void
  onUpdateFile: (data: { item_guid: string; file: File }) => void
  attachmentEditing: AttachmentEditing
  saveState: SaveState
  onSave: () => void
  isGeneratingPreview: boolean
  isUnlinking: boolean
  onGeneratePreview: (file: FileItem) => void
  isDownloading: boolean
  onDownload: (file: FileItem) => void
  onUnlink: (file: FileItem) => void
  /** Page count of the file shown in the viewer, if loaded */
  pageCount?: number
  onPagesChange?: (itemGuid: string, pages: number[] | null) => void
  setCropConfig: (
    cropConfig: {
      croppedLeft: number
      croppedTop: number
      croppedRight: number
      croppedBottom: number
    } | null,
  ) => void
}

const PropertiesPane = ({
  selectedFile,
  selectedFolder,
  debouncedFileConfigSave,
  debouncedFolderConfigSave,
  onUpdateFile,
  setCropConfig,
  attachmentEditing,
  saveState,
  onSave,
  isGeneratingPreview,
  isUnlinking,
  onGeneratePreview,
  isDownloading,
  onDownload,
  onUnlink,
  pageCount,
  onPagesChange,
}: Props) => {
  const { uploadAttachment } = useDocumentAttachmentMutations(attachmentEditing.onAttachmentChanged)

  const isMarkdownPage = selectedFile?.template_key === 'markdown-page'

  return (
    <Stack flex={1} height="100%">
      <Section
        title="Eigenschaften"
        hideBorderBottom
        ui2
        titleAdornment={
          <AutosaveIndicator
            isSaving={saveState.isSaving}
            isLoading={saveState.isLoading}
            dirty={saveState.isDirty}
            onSaveNow={onSave}
            data-cy="document-manager-properties-autosave"
          />
        }
        actions={
          selectedFile && (
            <DocumentActions
              file={selectedFile}
              isGeneratingPreview={isGeneratingPreview}
              isUnlinking={isUnlinking}
              onGeneratePreview={() => onGeneratePreview(selectedFile)}
              isDownloading={isDownloading}
              onDownload={() => onDownload(selectedFile)}
              onUnlink={() => onUnlink(selectedFile)}
            />
          )
        }
      />
      <Stack sx={{ overflowY: 'auto', flex: 1, minHeight: 0, pb: 1.5 }}>
        {selectedFile && (
          <DropzoneBox
            onDrop={acceptedFiles => {
              const file = acceptedFiles[0]
              if (isMarkdownPage) {
                // Attaches the dropped file (replacing an attached one)
                uploadAttachment({ item: selectedFile, file })
              } else {
                onUpdateFile({ item_guid: selectedFile.guid, file })
              }
            }}
            // accept={{ [selectedFile.mimetype || '']: [], 'application/octet-stream': [] }}
            disabled={!!selectedFile.template_key && !isMarkdownPage}
          >
            <FilePropertiesForm
              file={selectedFile}
              debouncedFileConfigSave={debouncedFileConfigSave}
              setCropConfig={setCropConfig}
              attachmentEditing={attachmentEditing}
              pageCount={pageCount}
              onPagesChange={onPagesChange}
            />
          </DropzoneBox>
        )}

        {selectedFolder && (
          <SectionPropertiesForm item={selectedFolder} mutate={debouncedFolderConfigSave} />
        )}
      </Stack>
    </Stack>
  )
}

export default PropertiesPane
